<template>
  <modal
    v-if="show"
    width="920px"
    height="700px"
    bodyPadding="0"
    :title="modalTitle"
    @close="checkClose"
    :read-article-url="`https://docs.bemob.com/${$i18n.locale}/new-affiliate-network`"
  >
    <template v-if="step === 1">
      <div class="dialog-wrapper">
        <div class="details affiliate-network-dialog__panel affiliate-network-dialog__panel--details">
          <div class="subtitle">
            1. {{t('Affiliate network details')}}
          </div>

          <div class="p-16">
            <div>
              <workspace-select
                :form="form"
                :labelHelpText="t('Workspace allows to make the required affiliate network or other element accessible only by a specific member or group of members')"
                @change-workspace="onChangeWorkspace"
                :disabled="mode === 'edit'"
                :workspacesList="workspacesList"
              ></workspace-select>
            </div>
            <div v-bind="getIntroParams(2)">
              <ui-input
                v-model="form.name"
                class="step-z"
                :placeholder="t('Name')"
                :label="t('Name')+'*'"
                :label-help="t('Used for displaying in reports.')"
                :hide-details="2"
                :error-messages="errorsDict['name']"
              ></ui-input>
            </div>

            <div>
              <ui-copy-input
                v-model="postbackUrl"
                class
                :label="t('Postback URL')"
              ></ui-copy-input>
            </div>

            <div>
              <ui-copy-input
                v-model="securePostbackUrl"
                class="mt-4 mb-12"
                :label="t('Secure Postback URL')"
              ></ui-copy-input>
            </div>

            <div :class="{'plan-limits-warning__tags': showPlanLimitsWarning_tags}">
              <ui-select
                planLimits="tagsAffiliateNetworks"
                v-model="form.tags"
                :items="store.cache.tags['affiliate-networks'].data"
                item-text="name"
                :placeholder="t('Select or type tags...')"
                :label="t('Tags')"
                :label-help="t('Add personalized tags to search and navigate easy through affiliate networks list.')"
                multiple
                chips
                attach
                combobox
                multi-line
                :hide-details="2"
                :error-messages="errorsDict['tags']"
              >
                <template #no-data>
                  <no-tag-found></no-tag-found>
                </template>
              </ui-select>
              <button v-if="showPlanLimitsWarning_tags" class="plan-limits-warning__clear" @click="clearTags">
                {{t('Reset tags')}}
              </button>
              <ui-tooltip-icon
                class="plan-limits-warning__tooltip"
                preset="error"
                :text="t('This option is no longer active due to the subscription change to the plan which doesn’t include this option or this add-on is switched off in your subscriptions settings.')"
                v-if="showPlanLimitsWarning_tags"
              ></ui-tooltip-icon>
            </div>
          </div>
        </div>

        <div class="details affiliate-network-dialog__panel affiliate-network-dialog__panel--options">
          <div class="subtitle">
            2. {{t('Additional options')}}
          </div>

          <div class="p-16">
            <div class="checkbox__wrap">
              <ui-checkbox
                :label="t('Append BeMob click ID value')"
                class="mb-16"
                v-model="form.appendClickId"
                :help-tooltip="t('Automatically append BeMob click ID value to the offer URLs of this affiliate network for conversion tracking.')"
              ></ui-checkbox>
            </div>

            <div v-if="form.appendClickId" class="append-value-check mb-8">
              <ui-input
                v-model="form.clickIdParam"
                :label="t('Affiliate network query string parameter for BeMob click ID')"
                :label-help="t('HTTP GET parameter is used to pass BeMob click ID to your affiliate network. As usual for the CAKE platform is s2, for HasOffers - aff_sub2.')"
                :hide-details="2"
                :error-messages="errorsDict['clickIdParam']"
              ></ui-input>
            </div>
            <div class="checkbox__wrap">
              <ui-checkbox
                class="mb-16"
                v-model="form.usePostbackStatuses"
                :label="t('Use postback statuses')"
                :help-tooltip="t('This option allows to pass conversion statuses from your affiliate network.')"
              ></ui-checkbox>
            </div>

            <div v-if="form.usePostbackStatuses" class>
              <ui-info class="mb-16">
                {{t('To set up the postback for tracking custom conversions it is required to save the current settings of affiliate network and move on to Setting -> Custom Conversions')}}
              </ui-info>

              <table class="use-postback-table">
                <thead class="use-postback-table__head">
                  <tr>
                    <th>{{t('Postback status')}}</th>
                    <th>{{t('Values')}}</th>
                  </tr>
                </thead>
                <tbody class="use-postback-table__body">
                  <tr>
                    <td>
                      <span>{{t('____x.new')}}</span>
                    </td>
                    <td>
                      <ui-input
                        v-model="form.postbackStatusNew"
                        :placeholder="t('Comma-separated values, e.g. new,created')"
                        :hide-details="1"
                        :error-messages="errorsDict['postbackStatusNew']"
                      ></ui-input>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <span>
                        {{t('Approved')}}
                        <ui-icon size="14" :color="$colors.green" icon="fas fa-dollar-sign"></ui-icon>
                        <ui-icon size="14" :color="$colors.green" class="ml-4 mr-8" icon="fas fa-sort-up"></ui-icon>
                        <ui-tooltip-icon
                          preset="help"
                          :text="t('Conversions with this status will be marked as confirmed and conversion revenue will be added to the report.')"
                        ></ui-tooltip-icon>
                      </span>
                    </td>
                    <td>
                      <ui-input
                        v-model="form.postbackStatusConfirm"
                        :placeholder="t('Comma-separated values, e.g. new,created')"
                        :hide-details="1"
                        :error-messages="errorsDict['postbackStatusConfirm']"
                      ></ui-input>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <span>{{t('Declined')}}</span>
                    </td>
                    <td>
                      <ui-input
                        v-model="form.postbackStatusDecline"
                        :placeholder="t('Comma-separated values, e.g. new,created')"
                        :hide-details="1"
                        :error-messages="errorsDict['postbackStatusDecline']"
                      ></ui-input>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <span>
                        {{t('Chargeback')}}
                        <ui-icon size="14" :color="$colors.red" icon="fas fa-dollar-sign"></ui-icon>
                        <ui-icon size="14" :color="$colors.red" class="ml-4 mr-8" icon="fas fa-sort-down"></ui-icon>
                        <ui-tooltip-icon
                          preset="help"
                          :text="t('Conversions with this status will be marked as charged and conversion revenue will be subtracted from the reports.')"
                        ></ui-tooltip-icon>
                      </span>
                    </td>
                    <td>
                      <ui-input
                        v-model="form.postbackStatusChargeback"
                        :placeholder="t('Comma-separated values, e.g. new,created')"
                        :hide-details="1"
                        :error-messages="errorsDict['postbackStatusChargeback']"
                      ></ui-input>
                    </td>
                  </tr>
                </tbody>
              </table>
              <div>
                <ui-select
                  v-model="form.postbackStatusDefault"
                  :placeholder="t('Default postback status')"
                  :label="t('Default postback status')"
                  :label-help="t('In case the received conversion can not be specified by any status from the fields above, BeMob will set the selected status for this conversion.')"
                  :items="defaultStatus"
                  item-text="name"
                  item-value="val"
                  :return-object="false"
                  :hide-details="2"
                  max-width-calc
                  :error-messages="errorsDict['postbackStatusDefault']"
                ></ui-select>
              </div>
            </div>
            <div class="checkbox__wrap">
              <ui-checkbox
                :label="t('Accept double postbacks')"
                class="mb-16"
                v-model="form.acceptDoublePostbacks"
                :help-tooltip="t('Using this option allows conversions with the same BeMob click Id to be reported several times. To track several conversions with the same BeMob сlick ID we recommend to use the ’txid’ parameter (Transaction ID) instead.')"
              ></ui-checkbox>
            </div>
            <div class="checkbox__wrap">
              <ui-checkbox
                :label="t('Accept postbacks from white IPs only')"
                :help-tooltip="t('This option enables an IP filter for postbacks from this network. The conversions data will be accepted only from the listed IP addresses.')"
                v-model="form.postbackOnlyWhiteIps">
              </ui-checkbox>
            </div>
            <div v-if="form.postbackOnlyWhiteIps">
              <span class="textarea-tooltip">
                <ui-icon size="12" icon="fa fa-info-circle"></ui-icon>
                {{t('Enter one or multiple IP addresses or network ranges (one per line): 20.30.40.50 or 20.30.40.50/24')}}
              </span>
              <ui-textarea
                v-model="form.postbackWhiteIps"
                :error-messages="errorsDict['postbackOnlyWhiteIps']"
              ></ui-textarea>
            </div>
            <div class="checkbox__wrap mt-16">
              <div
                :class="{
                  'plan-limits-warning__mask': form.hasCustomParamMasking && hasPlanLimitsRestriction('trafficSourceParamMasking'),
                }">
                <ui-checkbox
                  :planLimitsEditable="form.hasCustomParamMasking"
                  planLimits="trafficSourceParamMasking"
                  :label="t('Double-mask Traffic Source Params (Works With Ghost ID Only)')"
                  :help-tooltip="t('Enables double-masking of values received from the traffic source in custom parameters for this affiliate network.<br>Works only with Ghost ID in traffic source settings.')"
                  v-model="form.hasCustomParamMasking">
                </ui-checkbox>
                <ui-tooltip-icon
                  class="plan-limits-warning__tooltip"
                  preset="error"
                  :text="t('This option is no longer active due to the subscription change to the plan which doesn’t include this option or this add-on is switched off in your subscriptions settings.')"
                  v-if="form.hasCustomParamMasking && hasPlanLimitsRestriction('trafficSourceParamMasking')"
                ></ui-tooltip-icon>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <section
        class="justify-space-between xs12 align-center"
        style="display: flex; width: 100%"
        v-if="step === 1"
      >
        <div>
          <error-aggregator
            v-model="errors"
            v-model:active="errorsControlActive"
          ></error-aggregator>
        </div>
        <div class="d-flex justify-end align-center">
          <ui-update-info v-if="mode === 'edit'" :data="form"></ui-update-info>
          <ui-button
            :text="t('Cancel')"
            icon="fas fa-times"
            :disabled="submitLoading"
            @click="checkClose"
          ></ui-button>

          <ui-button
            :text="t('Save')"
            icon="fas fa-check"
            button-style="accent"
            :disabled="isActionsDisabled"
            :loading="submitLoading"
            v-bind="getIntroParams(3)"
            @click="submit(); tutorial.close();"
          ></ui-button>
        </div>
      </section>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { useColors } from '@/composables';
import WorkspaceSelect from '@/views/reports/pickers/wokrspace-select.vue';
import NoTagFound from '@/views/reports/views/no-tag-found.vue';
import { computed } from 'vue';
import {
  useAffiliateNetworkDialog,
  type AffiliateNetworkDialogData,
} from './useAffiliateNetworkDialog';

const $colors = useColors();

const props = withDefaults(defineProps<{
  data?: AffiliateNetworkDialogData;
}>(), {
  data: () => ({}),
});

const emit = defineEmits<{
  close: [];
}>();

const mode = computed(() => props.data.mode ?? 'create');

const {
  store,

  t,
  show,
  step,
  submitLoading,
  form,
  errors,
  errorsControlActive,
  errorsDict,
  modalTitle,
  workspacesList,
  defaultStatus,
  isActionsDisabled,
  showPlanLimitsWarning_tags,
  postbackUrl,
  securePostbackUrl,
  hasPlanLimitsRestriction,
  tutorial,
  onChangeWorkspace,
  checkClose,
  clearTags,
  submit,
  getIntroParams,
} = useAffiliateNetworkDialog(mode.value, () => props.data, () => emit('close'));
</script>

<style lang="scss" scoped>
@import '@/styles/dialogs/report-modals';

.update-info {
  display: flex;

  :deep(.name) {
    max-width: 250px;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
}

@media (max-width: 900px) {
  .update-info {
    display: none;
  }
}

.plan-limits-warning__mask{
  position: relative;
  label{
    padding-right:30px;
  }
  &:before{
    content:'';
    display:block;
    border:1px solid var(--color-red);
    position: absolute;
    top: -8px;
    bottom: -2px;
    left: -8px;
    right: -4px;
  }
  .plan-limits-warning__tooltip{
    right: 9px;
    top: -5px;
  }
}
</style>

<style lang="scss">
@import '@/styles/dialogs/affiliate-network-dialog';
</style>
